import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

import { ReportCardPage } from "@/components/report-card/report-card";
import { ReportCardCompactHeader } from "@/components/report-card/report-card-compact-header";
import { combinedToReportCardProps } from "@/lib/from-combined";
import { pb } from "@/lib/pb";
import type { ReportCardCombined } from "@/lib/report-card-helpers";
import { useDocumentTitle } from "@/lib/use-document-title";

type FullRcc = ReportCardCombined & {
  exam_name?: string;
  exam_total_marks?: number;
  exam_total_questions?: number;
  created?: string;
};

interface BatchRecord {
  id: string;
  name?: string;
  exam_year?: string;
}

function rcQuery(id: string, authId: string | undefined) {
  return {
    queryKey: ["reportcard", id, authId ?? "anon"] as const,
    queryFn: () => pb.collection("report_card_combined").getOne<FullRcc>(id),
  };
}

// Fetched separately because report_card_combined only carries batch id,
// not name/exam_year. Non-suspending so the header still paints if the
// batch record is missing or slow.
function batchQuery(batchId: string | undefined, authId: string | undefined) {
  return {
    queryKey: ["batch", batchId ?? "none", authId ?? "anon"] as const,
    enabled: Boolean(batchId),
    queryFn: () =>
      pb.collection("batch").getOne<BatchRecord>(batchId as string, {
        fields: "id,name,exam_year",
      }),
  };
}

export const Route = createFileRoute("/_app/reportcards/$reportCardId")({
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(rcQuery(params.reportCardId, pb.authStore.model?.id)),
  component: ReportCardDetailPage,
});

function ReportCardDetailPage() {
  const { reportCardId } = Route.useParams();
  const authId = pb.authStore.model?.id;
  const { data: rc } = useSuspenseQuery(rcQuery(reportCardId, authId));
  const { data: batch } = useQuery(batchQuery(rc.batch, authId));
  useDocumentTitle(rc.exam_name ?? "Report card");

  return (
    <div className="flex flex-col gap-4">
      <ReportCardCompactHeader rc={rc} batch={batch} />
      <ReportCardPage {...combinedToReportCardProps(rc)} subjectsDetailed />
    </div>
  );
}
